import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import Select from 'tio-reimagined/components/select';
import { restartableTask, timeout } from 'ember-concurrency';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { task } from 'reactiveweb/ember-concurrency';
import type { ApplicationApprovalSummaryApprover } from '../../../types/tuition-assistance.ts';
import type EmployeeModel from '../../../models/employee.ts';
import type IntlService from 'ember-intl/services/intl';
import type StoreService from '@ember-data/store';
import type TasApplicationModel from '../../../models/tas-application.ts';
import type TasProgramInstance from '../../../models/tas-program-instance.ts';
import type { TaskInstance } from 'reactiveweb/ember-concurrency';
import type { NotificationsService } from 'tio-ui/components/notifications';

const INPUT_DEBOUNCE = /* config.environment === 'test' ? 0 : */ 250;

interface ReassignModalSignature {
  show: boolean;
  onClose: () => void;
  approverToReassign: ApplicationApprovalSummaryApprover;
  application: TasApplicationModel | TasProgramInstance;
  companyId: string;
}

export default class ReassignModalComponent extends Component<ReassignModalSignature> {
  @service declare intl: IntlService;
  @service declare store: StoreService;
  @service declare notifications: NotificationsService;

  @tracked selectedApproverId = '';
  @tracked searchTerm = '';

  onSearch = (value: string) => {
    this.searchTerm = value;
  };

  handleSelectionChange = async (key: string | null) => {
    this.selectedApproverId = key || '';
  };

  insertReviewer = dropTask(async () => {
    if (!this.selectedApproverId) return;
    const adapter =
      this.args.application.modelName === 'tas-application'
        ? this.store.adapterFor('tas-application')
        : this.store.adapterFor('tas-program-instance');

    return adapter.insertReviewer(
      this.args.application,
      this.selectedApproverId,
      this.args.approverToReassign.ordinal
    );
  });

  searchApprovers = restartableTask(async (search: string) => {
    if (!search) {
      return [];
    }
    await timeout(INPUT_DEBOUNCE);
    const results = await this.store.query<EmployeeModel>('employee', {
      filter: { company_id: this.args.companyId, search },
    });
    return results.map((employee) => ({
      key: employee.id!,
      label: employee.fullName || employee.id!,
    }));
  });

  fetchApproversTask: TaskInstance<Array<{ key: string; label: string }>> = task(
    this,
    this.searchApprovers,
    () => [this.searchTerm]
  );

  get items() {
    return this.fetchApproversTask.value ?? [];
  }

  onClose = () => {
    this.selectedApproverId = '';
    this.searchTerm = '';
    this.args.onClose();
  };

  submit = async () => {
    if (!this.selectedApproverId) return;

    try {
      await this.insertReviewer.perform();
      this.notifications.add('Successfully reassigned approver', {
        appearance: 'success',
      });
      this.onClose();
    } catch (e) {
      console.error(e);
      this.notifications.add('Something went wrong', {
        appearance: 'error',
      });
    }
  };

  <template>
    <Modal @isOpen={{@show}} @onClose={{this.onClose}} class="min-h-64">
      <:header>{{t "tuition_assistance.approval_queue.reassign"}}</:header>
      <:body>
        <div class="flex flex-col gap-y-2">
          <Select
            data-test-approver-select
            @label={{t "tuition_assistance.approval_queue.select_replacement_approver"}}
            @searchable={{true}}
            @onSearch={{this.onSearch}}
            @isLoading={{this.fetchApproversTask.isRunning}}
            @selected={{this.selectedApproverId}}
            @items={{this.items}}
            @onChange={{this.handleSelectionChange}}
            @clearable={{true}}
          />
        </div>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.insertReviewer.isRunning}}
          @onCancel={{this.onClose}}
          @onSubmit={{this.submit}}
        />
      </:footer>
    </Modal>
  </template>
}
